/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import produce from "immer";
import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams, NavLink, Link, useLocation } from "react-router-dom";
import { LayoutContext } from "../../components/layout";
import Pagination from "../../components/Pagination";
import fetchHelper from "../../services/fetchHelper";
import AllReportsFilter from "./AllReportsFilter";
import AllReportsFilterV2 from "./AllReportsFilterV2";
import { mapFilterKeys, mapKeyFilters } from "./contantsIntegrationList";
import useError from "../../hooks/useError";
import {
  getInterrogationDate,
  getNintyDayBillStatus,
  getStatus,
  getThirtyDayBillStatus,
  renderPriorityFlag,
  getDefaultSort,
} from "./AllReportsUtils";
import useFeatureFlag from "../../utils/featureFlag";
import classNames from "classnames";
import MyFilters from "./components/MyFilters";

import { ESIGN_ENHANCEMENT } from "../../constants/AppConfigConstants";
const PRIORITY_FLAG_ENABLED = "filter-priority-flag-enabled";
const CUSTOM_FILTERS_FLAG_ENABLED = "custom-filters-validation-enabled";
const PRIORITY_FLAG_ACTIVE_LOCATIONS = "active-locations-id";

const Loading = ({ length = 15 }) => {
  const array = new Array(length);
  array.fill(1);
  return array.map((item, index) => (
    <tbody key={`${index}-loading`}>
      <tr>
        <td colSpan="12"></td>
      </tr>
      <tr>
        <td colSpan="12" style={{ background: "#fff", padding: 0 }}>
          <img src="data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTUwNSA0NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KLnJlY3QgewpmaWxsOiB1cmwoI2xpbmVhci1ncmFkaWVudCk7Cn0KPC9zdHlsZT4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0ibGluZWFyLWdyYWRpZW50Ij4KPHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIzMyUiIHN0b3AtY29sb3I9IiNGN0Y3RjciIC8+CjxzdG9wIG9mZnNldD0iNTAlIiBzdG9wLWNvbG9yPSIjZWZmMGYwIiAvPgo8c3RvcCBvZmZzZXQ9IjY3JSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjRjdGN0Y3IiAvPgo8YW5pbWF0ZVRyYW5zZm9ybSBhdHRyaWJ1dGVOYW1lPSJncmFkaWVudFRyYW5zZm9ybSIKdHlwZT0idHJhbnNsYXRlIgpmcm9tPSItMSAwIgp0bz0iMSAwIgpiZWdpbj0iMHMiCmR1cj0iMS41cyIKcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgoKPHJlY3QgY2xhc3M9InJlY3QiIHg9IjAuOTgyOTEiIHk9IjAuMTY3NDgiIHdpZHRoPSIxNTAzLjE1IiBoZWlnaHQ9IjQ0LjgzMjUiIHJ4PSI2IiBmaWxsPSIjZWZmMGYwIi8+CjxyZWN0IGNsYXNzPSJyZWN0IiB4PSIwLjk4MjkxIiB5PSIwLjE2NzQ4IiB3aWR0aD0iMTUwMy4xNSIgaGVpZ2h0PSI0NC44MzI1IiByeD0iNiIgZmlsbD0idXJsKCNwYWludDBfbGluZWFyKSIvPgo8L3N2Zz4=" />
        </td>
      </tr>
    </tbody>
  ));
};

const AllReports = ({ activeTab, checkedItems, setCheckedItems }) => {
  const { user, currentLocation } = useContext(LayoutContext);
  const [filter, setFilter] = useState(null);
  const [defaultFilter, setDefaultFilter] = useState(null);
  const [textSearch, setTextSearch] = useState("");
  const [wildcard, setWildcard] = useState([]);
  const [showAdvance, setShowAdvance] = useState(false);
  const [data, setData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [to, setTo] = useState(null);
  const [sort, setSort] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [initOffest, setInitOffset] = useState(true);
  const [switchSortName, setSwitchSortName] = useState(null);
  const [switchSortManufacture, setSwitchSortManufacture] = useState(null);
  const [switchSortDeviceType, setSwitchSortDeviceType] = useState(null);
  const [switchSortInterrogationDate, setSwitchSortInterrogationDate] = useState(null);
  const [sortDisableName, setSortDisableName] = useState(true);
  const [sortDisableDeviceMFR, setSortDisableDeviceMFR] = useState(true);
  const [sortDisableDeviceType, setSortDisableDeviceType] = useState(true);
  const [sortDisableDate, setSortDisableDate] = useState(true);
  const [sortUpAndDown, setSortUpAndDown] = useState("headerSortUp");
  const { tab } = useParams();
  const LIMIT_PER_PAGE = 30;
  const showDefaultApiError = useError();
  const inputRef = useRef();
  const windowLocation = useLocation();
  const { isFeatureForLocationEnable } = useFeatureFlag();

  const isPriorityFlagEnabled = isFeatureForLocationEnable(
    ESIGN_ENHANCEMENT,
    PRIORITY_FLAG_ENABLED,
    PRIORITY_FLAG_ACTIVE_LOCATIONS,
    currentLocation.id
  );

  const isCustomFilterFlagEnabled = isFeatureForLocationEnable(
    ESIGN_ENHANCEMENT,
    CUSTOM_FILTERS_FLAG_ENABLED,
    PRIORITY_FLAG_ACTIVE_LOCATIONS,
    currentLocation.id
  );

  useEffect(() => {
    if (user) {
      setFilter(null);
      setOffset(0);
      setWildcard([]);
      disableSortValues();
      const location = localStorage.getItem("location");
      let textSearchValue = "";
      if (location) {
        textSearchValue =
          localStorage.getItem(activeTab + location) != null
            ? localStorage.getItem(activeTab + location)
            : "";
      } else {
        textSearchValue = localStorage.getItem(activeTab) != null ? localStorage.getItem(activeTab) : "";
      }
      if (textSearchValue) {
        setTextSearch(textSearchValue);
        onSearchSubmittion();
      } else {
        setTextSearch("");
      }
      setInitialLoad(true);
      setIsLoading(true);
      setCheckedItems([]);
      switch (activeTab) {
        case "poc":
          setDefaultFilter({
            "readingPhysician.id": { key: "readingPhysician.id", com: "$eq", value: user.id.toString() },
            interrogationStatus: { key: "interrogationStatus", com: "$lk", value: "PRELIMINARY" },
            interrogationType: { key: "interrogationType", com: "$eq", value: "POC" },
          });
          setSort(getDefaultSort(activeTab));
          return;
        case "remote":
          setDefaultFilter({
            "readingPhysician.id": { key: "readingPhysician.id", com: "$eq", value: user.id.toString() },
            interrogationStatus: { key: "interrogationStatus", com: "$lk", value: "PRELIMINARY" },
            interrogationType: { key: "interrogationType", com: "$lk", value: "REMOTE" },
          });
          setSort(getDefaultSort(activeTab));
          return;
        case "all":
          setDefaultFilter({});
          setSort(["-interrogationDate", "-id"]);
          return;
        case "unprocessed":
          setDefaultFilter({
            interrogationStatus: { key: "interrogationStatus", com: "$lk", value: "NOTASSIGNED" },
            nurseFollowUp: { key: "nurseFollowUp", com: "$eq", value: "false" },
          });
          setSort(getDefaultSort(activeTab));
          return;
        case "followup":
          setDefaultFilter({ nurseFollowUp: { key: "nurseFollowUp", com: "$eq", value: "true" } });
          setSort(getDefaultSort(activeTab));
          return;
        case "biotel":
          setDefaultFilter({
            "device.manufacturerName": { key: "device.manufacturerName", com: "$eq", value: "BIOTEL" },
          });
          setSort(getDefaultSort(activeTab));
          return;
        default:
          setDefaultFilter({
            "readingPhysician.id": { key: "readingPhysician.id", com: "$eq", value: user.id.toString() },
            interrogationStatus: { key: "interrogationStatus", com: "$lk", value: "PRELIMINARY" },
          });
          setSort(getDefaultSort(activeTab));
          return;
      }
    }
  }, [activeTab, user]);

  const disableSortValues = () => {
    setSortDisableName(true);
    setSortDisableDeviceMFR(true);
    setSortDisableDeviceType(true);
    setSortDisableDate(true);
  };

  const toggleUpAndDown = (toggleEvent) => {
    if (toggleEvent) {
      setSortUpAndDown("headerSortDown");
    } else {
      setSortUpAndDown("headerSortUp");
    }
  };

  const handleSort = (key) => {
    setInitialLoad(true);
    disableSortValues();
    setSwitchSortName(null);
    setSwitchSortManufacture(null);
    setSwitchSortDeviceType(null);
    setSwitchSortInterrogationDate(null);
    switch (key) {
      case "name":
        if (switchSortName == null) {
          setSortDisableName(false);
          setSwitchSortName(true);
          toggleUpAndDown(switchSortName);
          setSort(["patient.family", "patient.name", "id"]);
        } else if (switchSortName) {
          setSortDisableName(false);
          setSwitchSortName(false);
          toggleUpAndDown(switchSortName);
          setSort(["-patient.family", "-patient.name", "id"]);
        } else {
          setSwitchSortName(null);
          setSort(getDefaultSort(activeTab));
        }
        break;
      case "manufacturer":
        if (switchSortManufacture == null) {
          setSortDisableDeviceMFR(false);
          setSwitchSortManufacture(true);
          toggleUpAndDown(switchSortManufacture);
          setSort(["device.manufacturerName", "id"]);
        } else if (switchSortManufacture) {
          setSortDisableDeviceMFR(false);
          setSwitchSortManufacture(false);
          toggleUpAndDown(switchSortManufacture);
          setSort(["-device.manufacturerName", "id"]);
        } else {
          setSwitchSortManufacture(null);
          setSort(getDefaultSort(activeTab));
        }
        break;
      case "deviceType":
        if (switchSortDeviceType == null) {
          setSortDisableDeviceType(false);
          setSwitchSortDeviceType(true);
          toggleUpAndDown(switchSortDeviceType);
          setSort(["device.deviceType", "id"]);
        } else if (switchSortDeviceType) {
          setSortDisableDeviceType(false);
          setSwitchSortDeviceType(false);
          toggleUpAndDown(switchSortDeviceType);
          setSort(["-device.deviceType", "id"]);
        } else {
          setSwitchSortDeviceType(null);
          setSort(getDefaultSort(activeTab));
        }
        break;
      case "interrogationDate":
        if (switchSortInterrogationDate == null) {
          setSortDisableDate(false);
          setSwitchSortInterrogationDate(true);
          toggleUpAndDown(switchSortInterrogationDate);
          setSort(["interrogationDate", "id"]);
        } else if (switchSortInterrogationDate) {
          setSortDisableDate(false);
          setSwitchSortInterrogationDate(false);
          toggleUpAndDown(switchSortInterrogationDate);
          setSort(["-interrogationDate", "-id"]);
        } else {
          setSwitchSortInterrogationDate(null);
          setSort(getDefaultSort(activeTab));
        }
        break;
    }
  };

  const onSearchSubmittion = (textSearchValue) => {
    let filters = {};
    const location = localStorage.getItem("location");
    if (textSearchValue == "") {
      if (location) {
        localStorage.setItem(activeTab + location, "");
      } else {
        localStorage.setItem(activeTab, "");
      }
    }
    if (textSearchValue === undefined) {
      if (location) {
        textSearchValue =
          localStorage.getItem(activeTab + location) != null
            ? localStorage.getItem(activeTab + location)
            : "";
      } else {
        textSearchValue = localStorage.getItem(activeTab) != null ? localStorage.getItem(activeTab) : "";
      }
      setTextSearch(textSearchValue);
    }
    if (textSearchValue) {
      const item = (i) => mapKeyFilters[i.split(":")[0]];
      let textWithoutFilter = textSearchValue;
      textSearchValue.split(" ").forEach((i) => {
        if (i.split(":")[0] && i.split(":")[1] && item(i)?.key) {
          filters[item(i).key] = { key: item(i).key, com: item(i).com, value: i.split(":")[1] };
          textWithoutFilter = textWithoutFilter.replace(i, "").trim();
        }
      });
      if (textWithoutFilter !== "") {
        const sanitizeSearch = textWithoutFilter.replace(/\s\s+/g, " ");
        const wildCard = [{ [mapKeyFilters.wildcard.key]: { [mapKeyFilters.wildcard.com]: sanitizeSearch } }];
        setTextSearch(sanitizeSearch);
        setWildcard(wildCard);
      } else {
        setWildcard([]);
      }
      setIsLoading(true);
    } else {
      setOffset(0);
      setWildcard([]);
    }
    setFilter(filters);
  };

  useEffect(() => {
    fetchData();
  }, [sort]);

  useEffect(() => {
    if (!initOffest) {
      setInitialLoad(true);
      fetchData();
    }
  }, [offset]);

  useEffect(() => {
    const location = localStorage.getItem("location");
    if (filter) {
      setIsLoading(true);
      setInitialLoad(true);
      let textSearchInput = Object.keys(filter)
        .map((key) => `${mapFilterKeys[key]}:${filter[key].value}`)
        .join(" ");

      if (wildcard.length) {
        textSearchInput += ` ${wildcard[0]["patient.search"]["$lk"]}`;
      }

      const result = Object.keys(filter)
        .map((key) => `${mapFilterKeys[key]}:${filter[key].value}`)
        .join(" ");

      if (result) setTextSearch(textSearchInput);

      fetchData();
      if (result) {
        if (location) {
          localStorage.setItem(activeTab + location, result);
        } else {
          localStorage.setItem(activeTab, result);
        }
      } else {
        let textSearchValue = "";
        if (location) {
          textSearchValue =
            localStorage.getItem(activeTab + location) != null
              ? localStorage.getItem(activeTab + location)
              : "";
        } else {
          textSearchValue = localStorage.getItem(activeTab) != null ? localStorage.getItem(activeTab) : "";
        }
        setTextSearch(textSearchValue);
      }
      if (Object.keys(filter).length === 0) {
        if (textSearch.search(":") !== -1) setTextSearch("");
        else setTextSearch(textSearch);
      }
    } else setTextSearch(textSearch);
  }, [filter]);

  const fetchData = async () => {
    if (!sort) {
      return;
    }

    let insuranceSelected = false;
    let insuranceType = null;
    let isThereInvalidFilter = false;
    try {
      const where = [];
      Object.keys(defaultFilter || {}).map((f) => {
        const data = defaultFilter[f];
        where.push({ [data.key]: { [data.com]: data.value } });
      });
      if (wildcard) where.push(...wildcard);
      if (filter)
        Object.keys(filter).map((f) => {
          const data = filter[f];
          if (data.key != "patient.insurance.insuranceType") {
            let repeatedFilter = Object.keys(defaultFilter).find((current) => {
              const defaultFilterItem = defaultFilter[current];
              return defaultFilterItem.key == data.key && defaultFilterItem.value != data.value;
            });
            isThereInvalidFilter = isThereInvalidFilter || repeatedFilter != null;
            where.push({ [data.key]: { [data.com]: data.value } });
          } else {
            insuranceType = data.value;
            insuranceSelected = true;
          }
        });

      const urlParams = {
        where: JSON.stringify(where),
        match: "AND",
        matchInner: "OR",
        sort: sort.join(","),
      };

      if (insuranceSelected == true && insuranceType != null) {
        urlParams.insuranceType = insuranceType;
      }
      let data;
      if (!isThereInvalidFilter) {
        const urlSearch = new URLSearchParams(urlParams);
        localStorage.setItem("interrogationFilter", urlSearch);
        const response = await fetchHelper.get({
          url: `/patientmanagement/rest/interrogations/reportListView?${urlSearch}`,
          headers: {
            "X-Range": `items=${offset}-${offset + LIMIT_PER_PAGE - 1}`,
            Range: `items=${offset}-${offset + LIMIT_PER_PAGE - 1}`,
          },
          includeResponseHeaders: ["Content-Range"],
        });
        data = response.data;
      } else {
        data = [];
      }
      setTo(offset + data.length - 1);
      setData(data);
      tableData.length = 0;
      data.map((interrogation) => {
        let tableRow = {};
        tableRow.alert = interrogation.id;
        tableRow.id = interrogation.id;
        tableRow.priorityFlag = renderPriorityFlag(interrogation);
        tableRow.status = getStatus(interrogation);
        tableRow.patientId = `${interrogation.patient.id}`;
        tableRow.name = `${interrogation.patient.family} , ${interrogation.patient.name}`;
        tableRow.manufacturer = interrogation.device && interrogation.device.manufacturerName;
        tableRow.deviceType = interrogation.device && interrogation.device.deviceType;
        tableRow.followingPhysician =
          interrogation.patient &&
          interrogation.patient.followingPhysician &&
          (interrogation.patient.followingPhysician.family
            ? interrogation.patient.followingPhysician.family + ","
            : "-") +
            (interrogation.patient.followingPhysician.name
              ? interrogation.patient.followingPhysician.name
              : " ");
        tableRow.interrogationDate = getInterrogationDate(interrogation);
        tableRow.thirtyDayBillStatus = getThirtyDayBillStatus(interrogation);
        tableRow.nintyDayBillStatus = getNintyDayBillStatus(interrogation);
        tableRow.visitNumber = interrogation.visitNumber;
        tableRow.location =
          interrogation.location.name +
          (interrogation.patient.subLocation ? "/" + interrogation.patient.subLocation : "");
        tableRow.interrogationType = interrogation.interrogationType;
        tableData.push(tableRow);
      });
      data.length > 0 ? setTableData(tableData) : setTableData(data);
      setInitOffset(false);
    } catch (err) {
      showDefaultApiError(err);
      setTableData([]);
      console.log(err);
    } finally {
      setInitialLoad(false);
      setIsLoading(false);
    }
  };

  const btnClassAdvSearch = classNames({
    colBtnAdvSearch: !isCustomFilterFlagEnabled,
    btnsAdvSearch: isCustomFilterFlagEnabled,
  });

  return (
    <>
      <div className="content">
        <div className="searchAdv" id="showSearch">
          <div className="search">
            <div className="row rowStretch searchIn framing">
              <div className="col col-s-100 col-m-70 col-l-60 col60 searchInCol">
                <label>
                  <input
                    id="serach-text-field"
                    className="textFieldSearch"
                    type="text"
                    placeholder="Type any term(s) to begin search..."
                    value={textSearch}
                    ref={inputRef}
                    onChange={(e) => setTextSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        setTextSearch(e.target.value);
                        onSearchSubmittion(e.target.value);
                      }
                    }}
                  />
                  <button
                    id="serach-icon"
                    className="searchField"
                    onClick={(e) => {
                      e.preventDefault();
                      setTextSearch(inputRef.current.value);
                      onSearchSubmittion(inputRef.current.value);
                    }}
                  >
                    {isLoading ? (
                      <div
                        id="results-loading-icon"
                        className="saving-white-ico icon-64"
                        style={{ height: 18, width: 18, filter: "invert()" }}
                      />
                    ) : (
                      <div
                        className="search-icon icon-64"
                        style={{ filter: "invert()", width: "15px", height: "15px" }}
                      ></div>
                    )}
                  </button>
                </label>
              </div>
              <div
                className={classNames("col col-s-100 col-m-30 col-l-10", {
                  col10: !isCustomFilterFlagEnabled,
                  col20: isCustomFilterFlagEnabled,
                })}
              >
                <div className={btnClassAdvSearch}>
                  {isCustomFilterFlagEnabled ? (
                    <>
                      <MyFilters onFilterChange={(filter) => setFilter(filter)} type="IR_LIST_VIEW_FILTER" />
                      <button
                        className="btn btnTeal hideAdv"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowAdvance(!showAdvance);
                        }}
                      >
                        All Filters
                      </button>
                      <p
                        onClick={(e) => {
                          e.preventDefault();
                          const location = localStorage.getItem("location");
                          localStorage.removeItem(activeTab + location);
                          setTextSearch("");
                          onSearchSubmittion();
                        }}
                      >
                        Clear
                      </p>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btnTeal hideAdv"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-around",
                          maxWidth: "200px",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowAdvance(!showAdvance);
                        }}
                      >
                        <div id="advanced-search-btn" className="search-icon icon-64"></div>
                        {showAdvance ? "Hide Advanced" : "Show Advanced"}
                      </button>
                      <button
                        className="btn btnTeal hideAdv"
                        id="clear-adv-button"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-around",
                          maxWidth: "200px",
                        }}
                        onClick={(e) => {
                          const location = localStorage.getItem("location");
                          localStorage.removeItem(activeTab + location);
                          setTextSearch("");
                          onSearchSubmittion();
                          e.preventDefault();
                        }}
                      >
                        Clear
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          {showAdvance ? (
            isPriorityFlagEnabled ? (
              <AllReportsFilterV2
                onFilterChange={(filter) => setFilter(filter)}
                filter={filter}
                tab={activeTab}
              />
            ) : (
              <AllReportsFilter
                onFilterChange={(filter) => setFilter(filter)}
                filter={filter}
                tab={activeTab}
              />
            )
          ) : null}
        </div>
      </div>

      <div className="contentTbl">
        <table className="overview dataTable">
          <thead>
            <tr>
              {tab ? null : (
                <th>
                  <label className="formLi">
                    <input
                      id="batch-select-chk"
                      type="checkbox"
                      className="chk"
                      checked={data.length > 0 && checkedItems.length === data.length ? true : false}
                      onChange={(e) => {
                        const { checked } = e.target;
                        let checkedItems;
                        if (checked) checkedItems = data.map((i) => i.id);
                        else checkedItems = [];
                        setCheckedItems(checkedItems);
                      }}
                    />
                    <span className="chkmrk"></span>
                  </label>
                </th>
              )}
              <th id="alert-header">Alert</th>
              <th id="status-header">Status</th>
              <th id="last-first-sort" className={sortDisableName ? "sortDisable" : sortUpAndDown}>
                <a href="#" id="last-first-header" onClick={() => handleSort("name")}>
                  Last Name, First
                </a>
              </th>
              <th id="device-mfr-sort" className={sortDisableDeviceMFR ? "sortDisable" : sortUpAndDown}>
                <a href="#" id="device-mfr-header" onClick={() => handleSort("manufacturer")}>
                  Device MFR
                </a>
              </th>
              <th id="device-type-sort" className={sortDisableDeviceType ? "sortDisable" : sortUpAndDown}>
                <a href="#" id="device-type-header" onClick={() => handleSort("deviceType")}>
                  Device Type
                </a>
              </th>
              <th id="following-header">Following</th>
              <th id="interrogation-date-sort" className={sortDisableDate ? "sortDisable" : sortUpAndDown}>
                <a href="#" id="interrogation-date-header" onClick={() => handleSort("interrogationDate")}>
                  Date
                </a>
              </th>
              <th id="30day-header">30 Day</th>
              <th id="90day-header">90 Day</th>
              <th id="visit-header">Visit #</th>
              <th id="location-header">Location</th>
              <th id="interrogation-type-header">Type</th>
            </tr>
          </thead>

          {initialLoad ? (
            <Loading />
          ) : tableData.length > 0 ? (
            <tbody>
              {tableData.map((tableRow, index) => (
                <tr key={tableRow.id}>
                  {tab ? null : (
                    <td id="report-checkbox">
                      <label className="formLi">
                        <input
                          type="checkbox"
                          className="chk"
                          checked={checkedItems.some((i) => i === tableRow.id)}
                          onChange={(e) => {
                            const { checked } = e.target;
                            if (checked)
                              setCheckedItems((prev) =>
                                produce(prev, (draft) => {
                                  draft.push(tableRow.id);
                                })
                              );
                            else setCheckedItems((prev) => prev.filter((i) => i !== tableRow.id));
                          }}
                        />
                        <span className="chkmrk"></span>
                      </label>
                    </td>
                  )}
                  <td id="priority-flag">
                    <div>{tableRow.priorityFlag}</div>
                  </td>
                  <td id="interrogation-status">
                    <div className="statusLbl statusLblTealL">{tableRow.status}</div>
                  </td>
                  <td id="name">
                    <Link
                      to={`/patients3/${tableRow.patientId}`}
                      onClick={() => {
                        localStorage.setItem("master-location", `${windowLocation.pathname}?return=true`);
                      }}
                    >
                      {tableRow.name}
                    </Link>
                  </td>
                  <td id="manufacturer">{tableRow.manufacturer}</td>
                  <td id="device-type">{tableRow.deviceType}</td>
                  <td id="following-physician">{tableRow.followingPhysician}</td>
                  <td id="interrogation-date">{tableRow.interrogationDate}</td>
                  <td id="thirty-bill" style={{ minWidth: 95 }}>
                    {tableRow.thirtyDayBillStatus}
                  </td>
                  <td id="ninty-bill" style={{ minWidth: 95 }}>
                    {tableRow.nintyDayBillStatus}
                  </td>
                  <td id="visit-number">{tableRow.visitNumber}</td>
                  <td>{tableRow.location}</td>
                  <td id="interrogation-type">
                    <span>{tableRow.interrogationType}</span>
                    <div id={`view-btn-${index}`} className="show-options">
                      <NavLink
                        exact
                        to={`/dashboard3/${tableRow.id}`}
                        className="btn"
                        onClick={() => {
                          localStorage.setItem("inboxCurrentIndex", index + offset);
                        }}
                        id="view-button"
                      >
                        View
                      </NavLink>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : null}
        </table>
        {tableData.length === 0 && !initialLoad ? (
          <div
            id="no-reports"
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 300,
              fontSize: 18,
            }}
          >
            No reports
          </div>
        ) : null}
      </div>

      <Pagination
        offset={offset}
        to={to}
        limit={LIMIT_PER_PAGE}
        onSetOffset={setOffset}
        loading={initialLoad}
      />
    </>
  );
};

export default AllReports;
